/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useState } from 'react';
import {
  ClassOutput,
  DisciplineOutput,
  GroupedLessonsOutput,
  LessonInput,
  LessonInputUpdate,
  LessonOutput,
  SerieOutput,
} from '@/api/gql/graphql';
import { useGetClasses, useGetSeries } from '@/api/hooks';
import {
  useCreateLesson,
  useGetLessons,
  useRemoveLesson,
  useUpdateLesson,
} from '@/api/hooks/useLesson';
import { useToast } from '@/hooks/useToast';
import { Options } from '@/components/Common/Selects/Select.types';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { LessonFormValues, useHandleLessonForm } from '@/hooks/useLessonForm';
import { useLessonStore } from '@/store/LessonStore/LessonStore';
import { useClassesStore } from '@/store/ClassesStore/ClassesStore';
import { useSeriesStore } from '@/store/SeriesStore/SeriesStore';
import { createFile } from '@/api/fileUpload';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useStatusLessonModuleStore } from '@/store/StatusLessonModuleStore/StatusLessonModuleStore';
import { useDisciplineStore } from '@/store/DisciplineStore/DisciplineStore';
import { useInitializeSelectedDiscipline } from '@/store/TopicStore/TopicsStore';

export type LessonFormMode = 'create' | 'update';

export const useLessons = () => {
  const navigate = useNavigate();
  const {
    isOpenLessonModal,
    setIsOpenLessonModal,
    lesson,
    setLesson,
    lessonId,
    setLessonId,
    formMode,
    setFormMode,
    leconPlainText,
    leconHtmlText,
    resumePlainText,
    resumeHtmlText,
    setLeconPlainText,
    setLeconHtmlText,
    setResumePlainText,
    setResumeHtmlText,
    openLessonDeletionModal,
    lessonDeletionModal,
  } = useLessonStore();

  const { setSelectedDiscipline, selectedDiscipline } = useDisciplineStore();
  const { selectedSerieValues, setSelectedSerieValues } = useSeriesStore();
  const { selectedClassesValues, setSelectedClassesValues } = useClassesStore();
  const { selectedStatusValues, setSelectedStatusValues } =
    useStatusLessonModuleStore();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);

  const { showToast } = useToast();
  const { register, handleSubmit, errors, setValue, reset, trigger, control } =
    useHandleLessonForm('create');
  const {
    register: registerUpdateForm,
    handleSubmit: handleSubmitUpdate,
    errors: updateErrors,
    setValue: setUpdateValue,
    reset: resetUpdate,
    trigger: triggerUpdate,
    control: controlUpdate,
  } = useHandleLessonForm('update');

  const [discipline, setDiscipline] = useState<Options | null>({
    _id: '',
    name: '',
  });
  const [disciplines, setDisciplines] = useState<DisciplineOutput[]>([]);
  const [lessonsByDiscipline, setLessonsByDiscipline] = useState<
    LessonOutput[] | null
  >([]);
  const [allLessons, setAllLessons] = useState<GroupedLessonsOutput[]>([]);
  const [loadingLessons, setLoadingLessons] = useState<boolean>(false);
  const [isModalConfirmationLessonOpen, setIsModalConfirmationLessonOpen] =
    useState<boolean>(false);
  const { data: lessonsData, isLoading: loadingData } = useGetLessons({});
  const { data: classes } = useGetClasses({ name: '' });
  const { data: series } = useGetSeries({ classes: null, name: null });
  const { mutate: createLesson, isPending: isCreatePending } =
    useCreateLesson();
  const { mutate: updateLesson, isPending: isUpdatePending } =
    useUpdateLesson();

  const { mutate: removeLesson, isPending: isDeletePending } =
    useRemoveLesson();

  const htmlToText = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const openUpdateModal = () => {
    setFormMode('update');
    setIsOpenLessonModal(true);
  };

  const goToModule = () => {
    navigate(`/lessons/${lessonId}/new-module`);
  };

  const goToGenerator = () => {
    navigate(`/lessons/${lessonId}/generator`);
  };

  const handleGetLessonId = () => {
    setIsModalConfirmationLessonOpen(true);
    setLessonId(lessonId);
  };

  const openDeleteLesson = () => {
    openLessonDeletionModal();
  };

  const menuItems = [
    createMenuItem('Modifier', '', openUpdateModal),
    createMenuItem('Génèrateur', '', goToGenerator),
    createMenuItem('Module', '', goToModule),
    createMenuItem(`${lesson?.isPublished ? 'Cacher' : 'Publier'}`, '', () =>
      handleGetLessonId(),
    ),
    createMenuItem('Ecouter', '', () => alert('Écoute désactivée')),
    createMenuItem('Quizz', '', () => navigate(`/lessons/${lessonId}/quizz`)),
    createMenuItem('Evaluation', '', () =>
      navigate(`/lessons/${lessonId}/evaluation`),
    ),

    createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', openDeleteLesson),
  ];

  useEffect(() => {
    if (lessonsData && lessonsData?.length > 0) {
      setAllLessons(lessonsData);
      const disciplinesList = lessonsData.map(
        (item: { discipline: DisciplineOutput }) => item.discipline,
      );
      setDisciplines(disciplinesList);
      if (disciplinesList.length > 0 && !selectedDiscipline) {
        setSelectedDiscipline(disciplinesList[0]);
      }
    }
  }, [lessonsData]);

  useEffect(() => {
    setLoadingLessons(true);
    if (selectedDiscipline) {
      const filteredLessons = allLessons.find(
        (item) => item.discipline._id === selectedDiscipline._id,
      )?.lessons;

      if (lessonId) {
        const lesson = filteredLessons?.find((ls) => ls._id === lessonId);
        setLesson(lesson || null);
      }

      setLessonsByDiscipline(filteredLessons || []);
      setLoadingLessons(false);
    }
  }, [selectedDiscipline, allLessons, lessonId]);

  const handleChangeClasses = (values: Options[]) => {
    setSelectedClassesValues(values);
  };

  const handleChangeSeries = (values: Options[]) => {
    setSelectedSerieValues(values);
  };

  const handleChangeStatus = (values: Options[]) => {
    setSelectedStatusValues(values);
  };

  const handleChangeDiscipline = (value: any) => {
    setDiscipline(value);
  };

  const handleChangeLecon = useCallback(
    (content: string, editor: any) => {
      const textContent = editor.getContent({ format: 'text' });
      setLeconHtmlText(content);
      setLeconPlainText(textContent);

      setValue(
        'longContent',
        { html: content, text: textContent },
        { shouldValidate: false },
      );
      setUpdateValue(
        'longContent',
        { html: content, text: textContent },
        { shouldValidate: false },
      );
    },
    [setValue, setUpdateValue, setLeconHtmlText, setLeconPlainText],
  );

  const handleChangeResume = useCallback(
    (content: string, editor: any) => {
      const textContent = editor.getContent({ format: 'text' });
      setResumeHtmlText(content);
      setResumePlainText(textContent);
      setValue(
        'summary',
        { html: content, text: textContent },
        { shouldValidate: false },
      );
      setUpdateValue(
        'summary',
        { html: content, text: textContent },
        { shouldValidate: false },
      );
    },
    [setValue, setUpdateValue, setResumeHtmlText, setResumePlainText],
  );

  const cleanUp = () => {
    setIsOpenLessonModal(false);
    reset();
    setLeconHtmlText('');
    setLeconPlainText('');
    setResumeHtmlText('');
    setResumePlainText('');
    setValue('longContent', { html: '', text: '' });
    setValue('summary', { html: '', text: '' });
  };

  const classIdsToFilter = selectedClassesValues?.map((c) => c._id);
  const seriesIdsToFilter = selectedSerieValues?.map((c) => c._id);
  const statusIdsToFilter = selectedStatusValues?.map((c) => c._id);

  const filteredLessons = lessonsByDiscipline?.filter((lesson) => {
    const matchesClass =
      classIdsToFilter && classIdsToFilter?.length > 0
        ? lesson.classes?.some((cls: ClassOutput) =>
            classIdsToFilter.includes(cls._id),
          )
        : true;

    const matchesSeries =
      seriesIdsToFilter && seriesIdsToFilter?.length > 0
        ? lesson.series?.some((ser: SerieOutput) =>
            seriesIdsToFilter.includes(ser._id),
          )
        : true;

    const matchesStatus =
      statusIdsToFilter && statusIdsToFilter?.length > 0
        ? statusIdsToFilter.includes(lesson?.isPublished ? 'true' : 'false')
        : true;
    return matchesClass && matchesSeries && matchesStatus;
  });

  const uploadFile = async (
    file: File | undefined,
    token: string,
    errorMessage: string,
  ) => {
    if (!file) return Promise.resolve(null);

    const response = await axios(createFile(file, token));
    if (response.status !== 201) {
      throw new Error(errorMessage);
    }
    return response.data?.publicUrl || response.data;
  };

  const createLessonHandler = async (
    values: LessonFormValues,
    isPublished: boolean,
  ) => {
    setIsUploading(true);
    const token = localStorage.getItem('authToken') ?? '';
    const imageUploadPromise = uploadFile(
      values.picture,
      token,
      "Erreur lors de l'ajout de l'image",
    );
    const audioUploadPromise = uploadFile(
      values.audio,
      token,
      "Erreur lors de l'ajout de l'audio",
    );

    return Promise.all([imageUploadPromise, audioUploadPromise]).then(
      ([imageUrl, audioUrl]) => {
        const lesson: LessonInput = {
          name: values.title ?? '',
          classes: (values?.classe && values?.classe.map((cl) => cl._id)) || [],
          series: (values?.serie && values?.serie.map((sr) => sr._id)) || [],
          discipline: values.discipline,
          image: imageUrl ?? '',
          audioShortcut: audioUrl ?? '',
          isPublished,
          summary: {
            html: resumeHtmlText ?? '',
            text: resumePlainText ?? '',
          },
          longContent: {
            html: leconHtmlText ?? '',
            text: leconPlainText ?? '',
          },
        };

        return createLesson(lesson, {
          onSuccess: () => {
            setSelectedClassesValues([]);
            setSelectedSerieValues([]);
            setSelectedStatusValues([]);
            showToast('Lesson ajoutée', 'success', {
              autoClose: 3000,
              position: 'top-right',
            });
            setLessonId('');
            setIsOpenLessonModal(false);
            setIsUploading(false);
            setLeconHtmlText(''), setResumeHtmlText(''), reset();
            setValue('longContent', { html: '', text: '' });
            setValue('summary', { html: '', text: '' });
            setIsModalConfirmationLessonOpen(false);
          },
          onError: (error: { message: string }) => {
            showToast(error?.message, 'error', {
              autoClose: 3000,
              position: 'top-right',
            });
          },
        });
      },
    );
  };

  const onCreateLesson = handleSubmit(async (values: LessonFormValues) => {
    return createLessonHandler(values, true);
  });

  const onDraftLesson = handleSubmit(async (values: LessonFormValues) => {
    return createLessonHandler(values, false);
  });

  const onDeleteLesson = () => {
    removeLesson(
      {
        removeLessonId: lessonId,
      },
      {
        onSuccess: () => {
          showToast('Lesson supprimée', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          setLessonId('');
          openLessonDeletionModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const uploadUpdateFile = async (
    file: File | undefined,
    token: string,
    errorMessage: string,
  ) => {
    if (!file) return Promise.resolve(null);

    return axios(createFile(file, token)).then((response) => {
      if (response.status !== 201) {
        throw new Error(errorMessage);
      }
      return response.data?.publicUrl || response.data;
    });
  };
  console.log(lesson);
  const initUpdateForm = useCallback(() => {
    if (lesson && formMode === 'update') {
      setLeconHtmlText(lesson?.longContent?.html || '');
      setLeconPlainText(lesson?.longContent?.text || '');
      setResumeHtmlText(lesson?.summary?.html || '');
      setResumePlainText(lesson?.summary?.text || '');

      setUpdateValue('title', lesson?.name || '');
      setUpdateValue('discipline', lesson?.discipline?._id || '');
      setUpdateValue('longContent', {
        html: lesson?.longContent?.html || '',
        text:
          lesson?.longContent?.text ||
          (lesson?.longContent?.html
            ? htmlToText(lesson.longContent.html)
            : ''),
      });
      setUpdateValue('summary', {
        html: lesson?.summary?.html || '',
        text:
          lesson?.summary?.text ||
          (lesson?.summary?.html ? htmlToText(lesson.summary.html) : ''),
      });

      if (lesson.classes) {
        const classeValues = lesson.classes.map((c) => ({
          _id: c._id,
          name: c.name,
        }));
        setUpdateValue('classe', classeValues);
      }
      if (lesson.series) {
        const serieValues = lesson.series.map((s) => ({
          _id: s._id,
          name: s.name,
        }));
        setUpdateValue('serie', serieValues);
      }
    }
  }, [
    lesson,
    formMode,
    setUpdateValue,
    setLeconHtmlText,
    setLeconPlainText,
    setResumeHtmlText,
    setResumePlainText,
  ]);
  console.log(updateErrors);
  useEffect(() => {
    if (isOpenLessonModal && formMode === 'update' && lesson) {
      initUpdateForm();
    }
  }, [isOpenLessonModal, formMode, lesson, initUpdateForm]);

  const updateLessonHandler = async (
    values: LessonFormValues,
    lessonId: string,
    resetUpdate: () => void,
  ) => {
    const token = localStorage.getItem('authToken') ?? '';

    const imageUploadPromise = uploadUpdateFile(
      values.picture,
      token,
      "Erreur lors de l'ajout de l'image",
    );
    const audioUploadPromise = uploadUpdateFile(
      values.audio,
      token,
      "Erreur lors de l'ajout de l'audio",
    );

    return Promise.all([imageUploadPromise, audioUploadPromise]).then(
      ([imageUrl, audioUrl]) => {
        const lesson: LessonInputUpdate = {
          name: values.title ?? '',
          classes: (values.classe && values.classe.map((cl) => cl._id)) || [],
          series: (values.serie && values.serie.map((sr) => sr._id)) || [],
          summary: values.summary
            ? {
                html: values.summary.html || resumeHtmlText,
                text: values.summary.text || resumePlainText,
              }
            : null,
          longContent: values.longContent
            ? {
                html: values.longContent.html || leconHtmlText,
                text: values.longContent.text || leconPlainText,
              }
            : null,
          audioShortcut: audioUrl || null,
          image: imageUrl || null,
        };

        return updateLesson(
          {
            input: lesson,
            id: lessonId,
          },
          {
            onSuccess: () => {
              setSelectedClassesValues([]);
              setSelectedSerieValues([]);
              setSelectedStatusValues([]);
              showToast('Leçon mise à jour avec succès.', 'success', {
                autoClose: 3000,
                position: 'top-right',
              });
              setLessonId('');
              setIsOpenLessonModal(false);
              resetUpdate();
              setIsModalConfirmationLessonOpen(false);
              setLeconHtmlText(''), setResumeHtmlText(''), reset();
              setValue('longContent', { html: '', text: '' });
              setValue('summary', { html: '', text: '' });
            },
            onError: (error: { message: string }) => {
              showToast(
                error?.message || 'Erreur lors de la mise à jour.',
                'error',
                {
                  autoClose: 3000,
                  position: 'top-right',
                },
              );
            },
          },
        );
      },
    );
  };

  const onUpdateLesson = handleSubmitUpdate(
    async (values: LessonFormValues) => {
      return updateLessonHandler(values, lessonId, resetUpdate);
    },
  );

  const handleHideLesson = () => {
    return updateLesson(
      {
        input: {
          isPublished: false,
        },
        id: lessonId,
      },
      {
        onSuccess: () => {
          setIsModalConfirmationLessonOpen(false);
          setLessonId('');
          formMode === 'update' ? setIsOpenLessonModal(false) : null;
          showToast('Leçon cachée avec succès.', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          setLeconHtmlText(''), setResumeHtmlText(''), reset();
          setValue('longContent', { html: '', text: '' });
          setValue('summary', { html: '', text: '' });
        },
        onError: (error: { message: string }) => {
          showToast(
            error?.message || 'Erreur lors de la mise à jour.',
            'error',
            {
              autoClose: 3000,
              position: 'top-right',
            },
          );
        },
      },
    );
  };

  const handleShowLesson = () => {
    return updateLesson(
      {
        input: {
          isPublished: true,
        },
        id: lessonId,
      },
      {
        onSuccess: () => {
          setIsModalConfirmationLessonOpen(false);
          setLessonId('');
          formMode === 'update' ? setIsOpenLessonModal(false) : null;
          showToast('Leçon publiée avec succès.', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          setLeconHtmlText(''), setResumeHtmlText(''), reset();
          setValue('longContent', { html: '', text: '' });
          setValue('summary', { html: '', text: '' });
        },
        onError: (error: { message: string }) => {
          showToast(
            error?.message || 'Erreur lors de la mise à jour.',
            'error',
            {
              autoClose: 3000,
              position: 'top-right',
            },
          );
        },
      },
    );
  };

  const handleConfirmLessonAction = () => {
    if (lessonId && lesson?.isPublished) {
      handleHideLesson();
    } else if (lessonId && !lesson?.isPublished) {
      handleShowLesson();
    }
  };

  const handleValidateAndPublish = async () => {
    if (formMode === 'create') {
      const isValid = await trigger(undefined, { shouldFocus: true });
      if (isValid) {
        setIsModalConfirmationLessonOpen(true);
      }
    } else if (formMode === 'update') {
      const isValidUpdate = await triggerUpdate(undefined, {
        shouldFocus: true,
      });
      if (isValidUpdate) {
        setIsModalConfirmationLessonOpen(true);
      }
    }
  };

  useInitializeSelectedDiscipline(disciplines);

  return {
    disciplines,
    selectedDiscipline,
    lessonsByDiscipline,
    lessonId,
    setLessonId,
    lesson,
    loadingLessons,
    filteredLessons,
    allLessons,
    setSelectedDiscipline,
    loadingData,
    classes,
    series,
    discipline,
    isOpenLessonModal,
    setIsOpenLessonModal,
    handleChangeClasses,
    handleChangeSeries,
    handleChangeDiscipline,
    handleChangeLecon,
    handleChangeResume,
    cleanUp,
    handleValidateAndPublish,
    menuItems,
    activeTab,
    setActiveTab,
    handleSubmit,
    handleSubmitUpdate,
    errors,
    updateErrors,
    isUpdatePending,
    isCreatePending,
    reset,
    register,
    registerUpdateForm,
    setValue,
    control,
    controlUpdate,
    setUpdateValue,
    onCreateLesson,
    onDraftLesson,
    onUpdateLesson,
    formMode,
    setFormMode,
    resumeHtmlText,
    resumePlainText,
    leconHtmlText,
    leconPlainText,
    isUploading,
    setLeconHtmlText,
    setResumeHtmlText,
    handleChangeStatus,
    selectedStatusValues,
    isModalConfirmationLessonOpen,
    setIsModalConfirmationLessonOpen,
    handleConfirmLessonAction,
    onDeleteLesson,
    isDeletePending,
    openLessonDeletionModal,
    lessonDeletionModal,
  };
};
